/**
 * tickets-v2-editor-rules: the ticket group editor's save rule and sale-time conversion (CC 0477).
 */
/** Key-order-independent JSON, so two equal tier lists compare equal however the server ordered their keys. */
export function stableJson(v: unknown): string {
  if (Array.isArray(v)) return "[" + v.map(stableJson).join(",") + "]";
  if (v && typeof v === "object") return "{" + Object.keys(v as object).sort().map((k) => JSON.stringify(k) + ":" + stableJson((v as Record<string, unknown>)[k])).join(",") + "}";
  return JSON.stringify(v === undefined ? null : v);
}
/** Save is enabled when the SERVER says the draft differs from its base version, or the local comparison does.
 *  The server's word is taken from every draft read and every draft save (CC 0477). */
export function groupSaveEnabled(p: { saveState: string; serverDirty: boolean; localDiffers: boolean }): boolean {
  return p.serverDirty || p.localDiffers || p.saveState === "dirty" || p.saveState === "saving" || p.saveState === "needsMembers";
}
/** The sale picker answers an instant with the event zone's offset; the tier stores it in UTC, time included. */
export function saleInstantFromPicker(v: string | null | undefined): string | null {
  const ms = v ? Date.parse(v) : NaN;
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/**
 * A stored UTC instant expressed in the event's zone (CC 0490), e.g. "2026-10-22T00:00:00.000Z" in Europe/London →
 * "2026-10-22T01:00:00+01:00". A zoned DatePicker reads its value's LITERAL clock digits, so it must be handed this,
 * never the raw UTC string: a "…Z" value showed its UTC clock, an hour off in BST and 5–6 hours in Chicago.
 *
 * Midnight: a sale time at exactly 00:00 in the event's zone shows as the date alone. The picker's shared rule reads
 * "T00:00" as "no time", and its date-only value IS 00:00 in the zone, so it means the start of that day and
 * round-trips unchanged. The shared picker is not changed for this.
 *
 * Empty, unparseable or an unknown zone → the value unchanged (or null for empty).
 */
export function isoInZone(utcIso: string | null | undefined, tz: string | null | undefined): string | null {
  if (!utcIso) return null;
  const at = Date.parse(utcIso);
  if (!Number.isFinite(at) || !tz) return utcIso;
  try {
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(new Date(at)).map((x) => [x.type, x.value]));
    const wall = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
    const offMin = Math.round((wall - Math.floor(at / 1000) * 1000) / 60_000);
    const a = Math.abs(offMin);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${p.year}-${p.month}-${p.day}T${pad(+p.hour % 24)}:${p.minute}:${p.second}${offMin >= 0 ? "+" : "-"}${pad(Math.floor(a / 60))}:${pad(a % 60)}`;
  } catch {
    return utcIso;
  }
}
